'use client';

import { useEffect, useState } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { signIn } from 'next-auth/react';
import MenuIcon from '@mui/icons-material/Menu';
import {
  Box,
  Button,
  CircularProgress,
  Drawer,
  IconButton,
  Stack,
} from '@/primitives';
import { UserAvatar } from '@/components/avatars/UserAvatar';
import { AUTH_PROVIDER } from '@/constants/auth';
import {
  APP_ROUTE_NAMES,
  APP_ROUTE_PATHS,
  PUBLIC_ROUTES,
} from '@/configs/routes';
import { blue, slate, white } from '@/lib/colours';
import { focusRingOnDark, focusRingOnLight } from '@/lib/focusRing';
import { useResolvedColorScheme } from '@/hooks/useResolvedColorScheme';
import { COLOR_SCHEME } from '@/constants/colorScheme';
import { useAdminGuard } from '@/hooks/useAdminGuard';
import { usePrivate } from '@/components/private';
import { NAV_HEIGHT } from '@/configs/layout';
import Navigation from './Navigation';
import { useHeaderEntranceOnce } from './useHeaderEntranceOnce';

/** Past this scroll, type + a light contrast wash appear for sections below the hero. */
const SCROLL_TYPE_FLIP_PX = 56;

/** Hides an element on one side of the desktop/mobile breakpoint. */
const desktopOnly = { display: { xs: 'none', md: 'flex' } } as const;
const mobileOnly = { display: { xs: 'flex', md: 'none' } } as const;

/**
 * Landing-only (`/`) floating header: transparent over the hero; on scroll a
 * light wash + hairline appear for contrast. Other routes keep DesktopToolbar /
 * MobileToolbar unchanged.
 *
 * Desktop vs mobile is decided purely in CSS. `useShell().isMobile` only
 * resolves in an effect after mount, so branching on it here rendered the full
 * desktop nav on first paint and collapsed it into the hamburger a frame later.
 */
export default function HomeFloatingChrome() {
  const pathname = usePathname();
  const [overLight, setOverLight] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  // The landing chrome has no entrance animation of its own, but a full load
  // of `/` still counts as the site's first paint — consume the one-shot
  // toolbar entrance here so navigating to /search or /registry afterwards
  // doesn't replay the slide-up in the standard header.
  useHeaderEntranceOnce();

  const { isAuthenticated, isAuthLoading } = useAdminGuard(
    APP_ROUTE_PATHS.DASHBOARD,
  );
  const { userProfile, userProfileError } = usePrivate();
  const isAuthenticatedAwaitingProfile =
    isAuthenticated && !userProfile && !userProfileError;
  const { resolvedScheme } = useResolvedColorScheme();
  const isDarkScheme = resolvedScheme === COLOR_SCHEME.DARK;

  useEffect(() => {
    const readScrollTop = () => {
      const shell = document.getElementById('shell');
      return shell?.scrollTop ?? window.scrollY ?? 0;
    };

    const onScroll = () => {
      setOverLight(readScrollTop() > SCROLL_TYPE_FLIP_PX);
    };

    onScroll();
    const shell = document.getElementById('shell');
    shell?.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      shell?.removeEventListener('scroll', onScroll);
      window.removeEventListener('scroll', onScroll);
    };
  }, []);

  // On hero: transparent chrome + light type. After scroll the wash matches
  // the standard header's `background.paper` for the resolved scheme (white
  // in light mode, slate[800] in dark), so `/` scrolled looks like every
  // other route's bar. In dark mode the type stays light throughout.
  const onDark = !overLight || isDarkScheme;
  const linkColor = onDark ? slate[300] : slate[600];
  const linkActive = onDark ? slate[50] : blue[700];
  const logoSrc = onDark ? '/breedlogic-logo-dark.svg' : '/breedlogic-logo.svg';

  const navHrefActive = (href: string) =>
    href === PUBLIC_ROUTES.HOME
      ? pathname === PUBLIC_ROUTES.HOME
      : pathname === href || pathname.startsWith(`${href}/`);

  const focusRing = onDark ? focusRingOnDark : focusRingOnLight;
  const linkHover = onDark ? slate[50] : slate[900];

  const textNavSx = (active: boolean) =>
    ({
      position: 'relative',
      textTransform: 'none',
      fontWeight: 500,
      fontSize: '0.9375rem',
      whiteSpace: 'nowrap',
      flexShrink: 0,
      minWidth: 0,
      minHeight: 44,
      px: 1.25,
      py: 0,
      borderRadius: '8px',
      color: active ? linkActive : linkColor,
      backgroundColor: 'transparent',
      // The active marker is drawn relative to the label rather than to the
      // box, so growing the box to a 44px touch target doesn't strand the
      // underline halfway down the header.
      '&::after': {
        content: '""',
        position: 'absolute',
        left: 10,
        right: 10,
        top: 'calc(50% + 13px)',
        height: 2,
        borderRadius: 1,
        backgroundColor: active ? blue[500] : 'transparent',
      },
      '&:hover': {
        backgroundColor: 'transparent',
        color: active ? linkActive : linkHover,
      },
      '&:focus-visible': focusRing,
    }) as const;

  const signInSx = {
    textTransform: 'none',
    fontWeight: 600,
    fontSize: '0.875rem',
    minHeight: 36,
    px: 1.75,
    borderRadius: '8px',
    backgroundColor: blue[700],
    color: '#fff',
    boxShadow: 'none',
    '&:hover': {
      backgroundColor: blue[600],
      boxShadow: 'none',
    },
    '&:focus-visible': focusRing,
  } as const;

  // UserAvatar paints the name and caret with theme text colours, which are
  // dark-on-dark over the hero. The pill both re-tints them for the current
  // tone and gives the signed-in state a hit target distinct from the nav.
  const authPillSx = {
    display: 'flex',
    alignItems: 'center',
    flexShrink: 0,
    minWidth: 0,
    minHeight: 36,
    pl: 0.5,
    pr: 0.75,
    py: 0.5,
    borderRadius: '999px',
    border: '1px solid',
    borderColor: onDark ? `${slate[50]}33` : slate[200],
    backgroundColor: onDark ? `${slate[50]}14` : '#fff',
    transition: 'background-color 0.2s ease, border-color 0.2s ease',
    '&:hover': {
      backgroundColor: onDark ? `${slate[50]}26` : slate[100],
      borderColor: onDark ? `${slate[50]}4d` : slate[300],
    },
    '& #user-profile-button': { gap: '4px' },
    '& .MuiTypography-root': {
      color: onDark ? slate[100] : slate[800],
      fontWeight: 500,
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      maxWidth: 148,
    },
    '& .MuiSvgIcon-root': { color: onDark ? slate[300] : slate[500] },
  } as const;

  const signInButton = (
    <Button
      variant="contained"
      sx={signInSx}
      onClick={() => {
        void signIn(AUTH_PROVIDER, { callbackUrl: pathname });
      }}
    >
      Sign in
    </Button>
  );

  const isAuthResolved = !isAuthLoading && !isAuthenticatedAwaitingProfile;

  const authSlot = (
    <>
      {/* Reserve the sign-in button's footprint (73×40) while auth resolves,
          at both breakpoints, so the hamburger doesn't jump sideways when the
          CTA lands. */}
      {isAuthLoading && (
        <Box aria-hidden sx={{ width: 73, height: 40, flexShrink: 0 }} />
      )}
      {!isAuthLoading && isAuthenticatedAwaitingProfile && (
        <Box
          aria-label="Loading user profile"
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 73,
            height: 40,
            flexShrink: 0,
          }}
        >
          <CircularProgress size={18} thickness={4} sx={{ color: linkColor }} />
        </Box>
      )}
      {/* Signed in: desktop gets the avatar menu. On mobile the profile and
          its menu live in the drawer — inline in a 375px bar they overlapped
          the logo and hamburger. */}
      {isAuthResolved && isAuthenticated && (
        <Box sx={{ ...authPillSx, ...desktopOnly }}>
          <UserAvatar compact trailingIcon="chevron" />
        </Box>
      )}
      {isAuthResolved && !isAuthenticated && signInButton}
    </>
  );

  return (
    <>
      <Box
        component="header"
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          zIndex: 1300,
          backgroundColor: overLight
            ? `${isDarkScheme ? slate[800] : white[50]}f5`
            : 'transparent',
          borderBottom: overLight
            ? `1px solid ${isDarkScheme ? `${white[50]}1f` : slate[200]}`
            : '1px solid transparent',
          boxShadow: overLight
            ? `0 8px 24px -18px ${slate[900]}${isDarkScheme ? 'cc' : '55'}`
            : 'none',
          transition:
            'background-color 0.22s ease, border-color 0.22s ease, box-shadow 0.22s ease',
          '@media (prefers-reduced-motion: reduce)': {
            transition: 'none',
          },
          pointerEvents: 'none',
          '@media print': { display: 'none' },
        }}
      >
        <Box
          component="nav"
          aria-label="Primary"
          sx={{
            pointerEvents: 'auto',
            // Same grid as Navigation.tsx (`auto minmax(0, 1fr) auto`) with
            // the same height, padding and logo size, so `/` ⇄ /search ⇄
            // /registry only changes the bar's colours — nothing moves.
            display: 'grid',
            gridTemplateColumns: 'auto minmax(0, 1fr) auto',
            alignItems: 'center',
            columnGap: 2,
            width: '100%',
            height: NAV_HEIGHT,
            px: { xs: '12px', md: '24px' },
            backgroundColor: 'transparent',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexShrink: 0,
              width: { xs: 96, md: 112 },
              height: { xs: 30, md: 36 },
            }}
          >
            <Link
              href={PUBLIC_ROUTES.HOME}
              style={{
                display: 'flex',
                alignItems: 'center',
                width: '100%',
                height: '100%',
              }}
            >
              <Image
                alt="BreedLOGIC"
                src={logoSrc}
                width={176}
                height={37}
                priority
                style={{
                  objectFit: 'contain',
                  width: '100%',
                  height: '100%',
                }}
              />
            </Link>
          </Box>

          <Stack
            direction="row"
            spacing={0.5}
            sx={{
              ...desktopOnly,
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              minWidth: 0,
            }}
          >
            {(
              [
                [PUBLIC_ROUTES.HOME, APP_ROUTE_NAMES.HOME],
                [PUBLIC_ROUTES.SEARCH, APP_ROUTE_NAMES.SEARCH],
                [PUBLIC_ROUTES.REGISTRY_LANDING, APP_ROUTE_NAMES.REGISTRY],
              ] as const
            ).map(([href, label]) => (
              <Button
                key={href}
                component={Link}
                href={href}
                sx={textNavSx(navHrefActive(href))}
              >
                {label}
              </Button>
            ))}
          </Stack>

          <Stack
            direction="row"
            spacing={{ xs: 1, md: 1.5 }}
            sx={{ alignItems: 'center', flexShrink: 0, justifySelf: 'end' }}
          >
            {authSlot}
            <IconButton
              aria-label="Open menu"
              size="small"
              onClick={() => setDrawerOpen(true)}
              sx={{
                ...mobileOnly,
                width: 40,
                height: 40,
                color: onDark ? slate[100] : slate[800],
                backgroundColor: 'transparent',
                border: 'none',
                '&:hover': { backgroundColor: 'transparent' },
                '&:focus-visible': focusRing,
              }}
            >
              <MenuIcon sx={{ fontSize: 24 }} />
            </IconButton>
          </Stack>
        </Box>
      </Box>

      <Drawer
        anchor="left"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        sx={{
          // The landing header is fixed at 1300 so it clears the hero; the
          // default drawer layer (1200) sat under it, leaving the logo and
          // sign-in button painted on top of the open panel.
          zIndex: (theme) => theme.zIndex.modal + 1,
          '& .MuiDrawer-paper': {
            p: 0,
            width: '90%',
            maxWidth: 320,
            boxSizing: 'border-box',
            borderRadius: 0,
            padding: '12px 10px 10px',
            backgroundColor: 'background.paper',
            backgroundImage: 'none',
          },
          '@media print': { display: 'none' },
        }}
      >
        <Box sx={{ width: '100%' }}>
          <Navigation
            onNavigate={() => setDrawerOpen(false)}
            onClose={() => setDrawerOpen(false)}
          />
        </Box>
      </Drawer>
    </>
  );
}
